import { InPlaceEditorProps, Labeled, NumberField, NumberInput, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";
import { InputAdornment, Tooltip } from "@mui/material";
import { defaultCurrencyFormatterOptions } from "@/utils/formatters/currencyFormatter";
import { validateNonNegative } from "@/utils/validators/inPlaceValidators";

/**
 * In-place editor for currency/dollar fields.
 *
 * Displays a formatted number with a dollar sign using {@link NumberField},
 * and allows editing with {@link NumberInput} that includes a dollar sign prefix.
 * Wrapped in a {@link Labeled} component to optionally show a field label.
 *
 * @param source - The field name to bind to
 * @param label - Optional label displayed above the input
 * @param readOnly - If true, disables editing and shows display only
 * @param readOnlyReason - Optional tooltip explaining why the field is read-only
 * @param rest - Additional props passed to {@link InPlaceEditor} and {@link NumberInput}
 */
const InPlaceDollarEditor = ({
  source,
  label,
  readOnly = false,
  readOnlyReason,
  validate,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  readOnlyReason?: string;
  validate?: Validator | Validator[];
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Compose built-in non-negative validation with any user-supplied validators
  const effectiveValidate: Validator | Validator[] = validate
    ? [validateNonNegative, ...(Array.isArray(validate) ? validate : [validate])]
    : validateNonNegative;
  // Read-only mode: just show the formatted number
  if (readOnly) {
    return (
      <Tooltip title={readOnlyReason}>
        <span>
          <Labeled label={label}>
            <NumberField
              source={source}
              variant="body1"
              sx={{ display: "block", mb: 0.5 }}
              emptyText="-"
              options={defaultCurrencyFormatterOptions}
            />
          </Labeled>
        </span>
      </Tooltip>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <NumberInput
            {...rest}
            source={source}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={effectiveValidate ? undefined : false}
            validate={effectiveValidate}
            autoFocus
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <NumberField
          source={source}
          variant="body1"
          sx={{ display: "block", mb: 0.5 }}
          emptyText="-"
          options={defaultCurrencyFormatterOptions}
        />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceDollarEditor;
